import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Printer, ReceiptText, Undo2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Pagina } from '@/shared/api/cliente';
import type { CobroDelRegistro } from '@/shared/api/tipos';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla } from '@/shared/ui/Tabla';
import type { Columna } from '@/shared/ui/Tabla';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PanelDeAnulacion } from './PanelDeAnulacion';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { PanelDelCobro } from './PanelDelCobro';
import { useRecibo } from './useRecibo';
import { nombreDelEstadoDePago } from './estados';

const NOMBRE_DEL_MEDIO: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  deposito: 'Depósito',
};

const FILTROS = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'registrado', etiqueta: 'Pagados' },
  { valor: 'anulado', etiqueta: 'Anulados' },
] as const;

export function PaginaRegistroDeCobros() {
  const [buscar, setBuscar] = useState('');
  const [estado, setEstado] = useState<string>('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [mirando, setMirando] = useState<number | null>(null);
  const [anulando, setAnulando] = useState<CobroDelRegistro | null>(null);
  const recibo = useRecibo();

  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();

  const registro = useQuery({
    queryKey: ['registro-de-cobros', buscar, estado, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<CobroDelRegistro>>(
        `/cobros?buscar=${encodeURIComponent(buscar)}&estado=${estado}` +
          `&pagina=${pagina}&tamanio=${tamanio}`,
      ),
  });

  const columnas: Columna<CobroDelRegistro>[] = [
    {
      clave: 'comprobante',
      encabezado: 'Comprobante',
      contenido: (c) => (
        <span className="block min-w-0">
          <span
            className={`importe block truncate text-xs font-semibold ${
              c.estado === 'anulado' ? 'text-armazon-400 line-through' : 'text-armazon-800'
            }`}
          >
            {c.numeroComprobante ?? '—'}
          </span>
          <span className="block text-[10px] uppercase text-armazon-500">
            {enFecha(c.fechaPago)}
            {c.caja && ` · ${c.caja}`}
          </span>
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Quién pagó',
      contenido: (c) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-800">
            {c.cliente}
          </span>
          <span className="importe block text-[10px] text-armazon-500">{c.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      contenido: (c) => (
        <span
          className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
            c.estado === 'anulado' ? 'bg-red-50 text-red-700' : 'bg-marca-50 text-marca-700'
          }`}
        >
          {nombreDelEstadoDePago(c.estado)}
        </span>
      ),
    },
    {
      clave: 'quien',
      encabezado: 'Cajero recaudador',
      // El nombre, no el correo. El correo se queda en el dato -es lo que identifica a
      // quien cobro- pero en una pantalla que se imprime y se lleva a una reunion no
      // pinta nada.
      contenido: (c) => (
        <span
          title={c.recibidoPor}
          className="block truncate text-[11px] uppercase text-armazon-600"
        >
          {c.cajero}
        </span>
      ),
    },
    {
      clave: 'medio',
      encabezado: 'Medio',
      contenido: (c) => (
        <span className="text-[11px] uppercase text-armazon-600">
          {NOMBRE_DEL_MEDIO[c.metodo] ?? c.metodo}
        </span>
      ),
    },
    {
      clave: 'facturas',
      encabezado: 'Facturas',
      alinear: 'centro',
      contenido: (c) => (
        <span className="importe text-xs text-armazon-600">{c.cuantasFacturas}</span>
      ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (c) => (
        <span
          className={`importe text-xs font-bold ${
            c.estado === 'anulado' ? 'text-armazon-400 line-through' : 'text-armazon-900'
          }`}
        >
          ${c.total.toFixed(2)}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-24',
      contenido: (c) => (
        <span className="flex justify-end gap-1">
          <BotonDeFila icono={Eye} titulo="Ver el detalle" alPulsar={() => setMirando(c.id)} />
          <BotonDeFila
            icono={Printer}
            titulo="Imprimir el recibo de 58 mm"
            alPulsar={() => void recibo.abrir(c.id, c.numeroComprobante)}
            ocupado={recibo.preparando === c.id}
          />

          {puede('pagos.anular') && c.anulable && (
            <BotonDeFila
              icono={Undo2}
              titulo="Anular este cobro"
              alPulsar={() => setAnulando(c)}
              peligro
            />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={ReceiptText}
        color="indigo"
        titulo="Registro de cobros"
        subtitulo="Todos los cobros, de cualquier caja y cualquier día"
      />

      <Tabla
        datos={registro.data}
        cargando={registro.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={(p) => setPagina(p - 1)}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="cobros"
        iconoVacio={ReceiptText}
        mensajeVacio="Ningún cobro coincide"
        herramientas={
          <div className="flex flex-1 flex-wrap items-center gap-3">
            <CampoDeBusqueda
              valor={buscar}
              alCambiar={(v) => {
                setBuscar(v);
                setPagina(0);
              }}
              marcador="Comprobante, cédula o nombre…"
            />
            <span className="flex gap-1">
              {FILTROS.map((f) => (
                <button
                  key={f.valor}
                  type="button"
                  onClick={() => {
                    setEstado(f.valor);
                    setPagina(0);
                  }}
                  aria-pressed={estado === f.valor}
                  className={`h-9 rounded-lg border px-3 text-[11px] font-semibold uppercase transition-colors ${
                    estado === f.valor
                      ? 'border-marca-600 bg-marca-50 text-marca-700'
                      : 'border-armazon-200 text-armazon-500 hover:border-armazon-300'
                  }`}
                >
                  {f.etiqueta}
                </button>
              ))}
            </span>
          </div>
        }
      />

      <VistaPreviaDeImpresion
        abierto={recibo.recibo !== null}
        alCerrar={recibo.cerrar}
        titulo="Recibo de cobro"
        subtitulo={
          recibo.recibo?.comprobante
            ? `Nº ${recibo.recibo.comprobante} · Ticket de 58 mm`
            : 'Ticket de 58 mm'
        }
        fichero={recibo.recibo?.fichero ?? null}
        cargando={recibo.preparando !== null}
      />

      {mirando !== null && (
        <PanelDelCobro
          pagoId={mirando}
          abierto
          alCerrar={() => setMirando(null)}
          alImprimir={() => void recibo.abrir(mirando)}
          imprimiendo={recibo.preparando === mirando}
        />
      )}

      {anulando && (
        <PanelDeAnulacion
          cobro={anulando}
          abierto
          alCerrar={() => setAnulando(null)}
          alAnular={() => {
            void clienteDeConsultas.invalidateQueries({ queryKey: ['registro-de-cobros'] });
            void clienteDeConsultas.invalidateQueries({ queryKey: ['jornada-de-caja'] });

            void clienteDeConsultas.invalidateQueries({ queryKey: ['cobro-deuda'] });
            void clienteDeConsultas.invalidateQueries({ queryKey: ['cartera-resumen'] });
          }}
        />
      )}
    </>
  );
}

function BotonDeFila({
  icono: Icono,
  titulo,
  alPulsar,
  ocupado = false,
  peligro = false,
}: {
  icono: LucideIcon;
  titulo: string;
  alPulsar: () => void;
  ocupado?: boolean;
  peligro?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={ocupado}
      title={titulo}
      aria-label={titulo}
      className={`grid size-7 place-items-center rounded-lg text-armazon-400 transition-colors disabled:opacity-40 ${
        peligro ? 'hover:bg-red-50 hover:text-alerta' : 'hover:bg-armazon-100 hover:text-marca-700'
      }`}
    >
      <Icono className="size-4" aria-hidden />
    </button>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
